import type { AccountCampaign } from '../../types/accountCampaign';

// Ad Preview Stage 3 §3.2. Per-row eligibility gate for the bulk
// "Send Previews" action. A row can be sent only when it has a
// generated preview link to send -- the bulk-send modal then
// aggregates the eligible rows per contact.
//
// Eligibility per row (mirrors the server's bulkSend pre-flight
// rejection list):
//   - rowType === 'campaign' (unassigned / opt-out have no preview)
//   - campaignId set
//   - not soft-deleted
//   - latestPreviewLinkId set (a preview was generated)
//
// The gate intentionally does NOT check link expiry/revocation or
// contact eligibility -- those need server / contact-data reads. The
// server re-checks the link per row (expired/revoked rows land in
// rejectedRows) and the modal shows the aggregate recipient counts
// before the AM commits.
//
// Returns the eligible row IDs (subset of selectedIds) + a reason for
// the disabled tooltip when none qualify.

export type BulkSendGate = {
  eligibleIds: string[];
  reason: string | null;
};

export function resolveBulkSendTarget(
  selectedIds: string[],
  rows: AccountCampaign[],
): BulkSendGate {
  if (selectedIds.length === 0) {
    return { eligibleIds: [], reason: 'Select one or more rows.' };
  }
  const selectedSet = new Set(selectedIds);
  const selected = rows.filter((r) => selectedSet.has(r.id));
  const eligible = selected.filter(
    (r) =>
      r.rowType === 'campaign' &&
      r.campaignId !== null &&
      r.deletedAt === null &&
      !!r.latestPreviewLinkId,
  );
  if (eligible.length === 0) {
    // Distinguish "no campaign rows" from "campaign rows but no
    // preview links yet" so the tooltip points the AM at the fix.
    const anyCampaign = selected.some(
      (r) => r.rowType === 'campaign' && r.campaignId !== null && r.deletedAt === null,
    );
    return {
      eligibleIds: [],
      reason: anyCampaign
        ? 'Generate previews for the selected rows first.'
        : 'No selected rows have an assigned Campaign.',
    };
  }
  return { eligibleIds: eligible.map((r) => r.id), reason: null };
}
